/**
 * CoachStreakCard — iter 144.
 *
 * Small dashboard card that shows how many coach sessions the talent has
 * completed this week, plus how many of the three tools they've touched.
 * The goal is a gentle nudge to come back — never a hiring-outcome claim.
 *
 * Backend contract: `GET /api/professional/coach/streak` →
 *   {sessions_this_week, tools_touched, tools_available, last_used_at,
 *    week_start}
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, MessageSquare, Flame } from "lucide-react";
import api from "@/lib/api";

const encouragement = (sessions, tools) => {
    if (sessions === 0) return "Try one coach tool this week — even five minutes helps.";
    if (tools >= 3) return "Nice — you've touched all three tools this week.";
    if (sessions >= 3) return "You're on a roll. Keep the momentum.";
    return "Nice start. One more session this week keeps the streak going.";
};

const CoachStreakCard = () => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data: d } = await api.get("/professional/coach/streak");
                if (!cancelled) setData(d);
            } catch {
                if (!cancelled) setErr(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (err || !data) return null;
    const { sessions_this_week: sessions = 0, tools_touched: tools = 0, tools_available: total = 3 } = data;

    return (
        <div
            className="card-soft p-5 flex flex-col justify-between"
            data-testid="coach-streak-card"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <Flame size={14} className="text-emerald-500" />
                        <p className="overline">Coach this week</p>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-3xl font-heading font-semibold text-foreground" data-testid="coach-streak-sessions">
                            {sessions}
                        </span>
                        <span className="text-sm text-muted-foreground">session{sessions === 1 ? "" : "s"}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                        <span data-testid="coach-streak-tools">{tools}</span> of {total} tools touched
                    </div>
                </div>
                <MessageSquare size={16} className="text-muted-foreground" />
            </div>
            <p className="mt-3 text-xs text-muted-foreground" data-testid="coach-streak-nudge">
                {encouragement(sessions, tools)}
            </p>
            <Link
                to="/professional/coach-tools"
                data-testid="coach-streak-cta"
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline"
            >
                Open Coach Tools <Sparkles size={12} />
            </Link>
        </div>
    );
};

export default CoachStreakCard;
